import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ChatStore, ChatSessionState } from './types';
import type { Alert } from '@/modules/alerts/types';
import getApi from '@/services/GetApi';

const PLAN_SEPARATOR = '----------------------------------------';
const STREAM_FLUSH_MS = 155;

const streamControllers = new Map<string, AbortController>();

const getDefaultSession = (): ChatSessionState => ({
  messages: [],
  isStreaming: false,
  botStatus: null,
  isChatActive: false,
});

const notifyAnswerReady = (question: string) => {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;

  if (document.visibilityState === 'visible') return;

  const trimmedQuestion = question.trim();
  const body =
    trimmedQuestion.length > 0
      ? `Your answer to "${trimmedQuestion.slice(0, 60)}${trimmedQuestion.length > 60 ? '…' : ''}" is ready.`
      : 'Your AIDA answer is ready. Click to open AIDA.';

  try {
    const notification = new Notification('AIDA answer ready', {
      body,
      tag: 'aida-answer-ready',
      renotify: true,
      icon: '/static/images/eyesAida.svg',
    });

    notification.onclick = () => {
      window.focus();
      window.location.assign('/Assistant');
      notification.close();
    };
  } catch (error) {
    console.warn('AIDA notification could not be displayed', error);
  }
};

const emitInAppAnswerReadyAlert = (question: string, chatId: string) => {
  if (typeof window === 'undefined') return;

  const trimmedQuestion = question.trim();
  const message =
    trimmedQuestion.length > 0
      ? trimmedQuestion.length > 60
        ? `${trimmedQuestion.slice(0, 57)}...`
        : trimmedQuestion
      : 'Your AIDA answer is ready.';

  const alertPayload: Alert = {
    id: `aida-ready-${Date.now()}`,
    type: 'aida',
    title: 'AIDA answer ready',
    message,
    target: 'all',
    priority: 1,
    buttons: [
      {
        text: 'Go to chat',
        action: 'link',
        url: `/Assistant?chatId=${encodeURIComponent(chatId)}`, // ← aquí
        style: 'primary',
        dismissOnClick: true,
      },
    ],
  };

  window.dispatchEvent(new CustomEvent<Alert>('enqueueAlert', { detail: alertPayload }));
};

const isInAidaView = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/Assistant');

const normalizeTopicFromQuestion = (question: string): string => {
  const cleaned = question.replace(/\s+/g, ' ').trim();
  const withoutOrdering = cleaned.replace(/\s+order(?:ed)?\s+by[\s\S]*$/i, '').trim();
  return withoutOrdering.replace(/\?$/, '').trim();
};

const toReasoningStep = (tool: string, question: string): string | null => {
  const normalizedTool = tool.trim().toLowerCase();
  const cleanQ = question.replace(/\s+/g, ' ').replace(/\?$/, '').trim();

  if (normalizedTool === 'disgenetapi' || normalizedTool === 'chemdisgenet') {
    const topic = normalizeTopicFromQuestion(question);
    return `Search in DISGENET: ${topic}`;
  }

  if (normalizedTool === 'summarizer' || normalizedTool === 'writer') {
    const label = cleanQ.charAt(0).toUpperCase() + cleanQ.slice(1);
    return label || 'Summarize results';
  }

  if (normalizedTool === 'redactor') {
    return null;
  }

  return `${tool}: ${cleanQ}`;
};

const parseReasoningSteps = (planText: string): string[] => {
  const taskRegex =
    /\*\*\s*Task ID:\s*\d+\s*\|\s*[\s\S]*?Tool:\s*([^\n*]+)\*\*[\s\S]*?question:\s*([^\n]+(?:\n(?!\s*\*\*\s*Task ID:)[^\n]+)*)/gi;
  const steps: string[] = [];

  for (const match of planText.matchAll(taskRegex)) {
    const tool = match[1]?.trim();
    const question = match[2]?.replace(/\s+/g, ' ').trim();

    if (!tool || !question) continue;
    const step = toReasoningStep(tool, question);
    if (step) steps.push(step);
  }

  return steps;
};

const parsePlanAndAnswer = (
  rawText: string,
): { reasoning: string; reasoningSteps: string[]; answer: string } => {
  const planIndex = rawText.indexOf('Plan:');
  const separatorIndex = rawText.indexOf(PLAN_SEPARATOR);

  if (planIndex === -1 || separatorIndex === -1 || separatorIndex < planIndex) {
    return { reasoning: '', reasoningSteps: [], answer: rawText };
  }

  const reasoning = rawText.slice(planIndex + 'Plan:'.length, separatorIndex).trim();
  const reasoningSteps = parseReasoningSteps(reasoning);
  const answer = rawText.slice(separatorIndex + PLAN_SEPARATOR.length).trimStart();

  return { reasoning, reasoningSteps, answer };
};

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      chatHistory: [],
      chatSessions: {},
      aidaNotificationsEnabled: false,

      setChatHistory: history => {
        set(state => ({
          chatHistory: typeof history === 'function' ? history(state.chatHistory) : history,
        }));
      },

      setAidaNotificationsEnabled: enabled => {
        set({ aidaNotificationsEnabled: enabled });
      },

      clearChatHistory: () => {
        set({ chatHistory: [] });
      },

      ensureSession: (chatId, initialBotMessage) => {
        set(state => {
          if (state.chatSessions[chatId]) return state;
          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: { ...getDefaultSession(), messages: [initialBotMessage] },
            },
          };
        });
      },

      getSessionMessages: chatId => get().chatSessions[chatId]?.messages ?? [],

      addUserMessage: (chatId, message) => {
        set(state => {
          const session = state.chatSessions[chatId] ?? getDefaultSession();
          const updatedMessages = [...session.messages, message];
          return {
            chatHistory: updatedMessages,
            chatSessions: {
              ...state.chatSessions,
              [chatId]: { ...session, messages: updatedMessages, isChatActive: true },
            },
          };
        });
      },

      clearSession: (chatId, initialBotMessage) => {
        set(state => ({
          chatHistory: [initialBotMessage],
          chatSessions: {
            ...state.chatSessions,
            [chatId]: { ...getDefaultSession(), messages: [initialBotMessage] },
          },
        }));
      },

      deleteSession: chatId => {
        set(state => {
          const { [chatId]: _, ...rest } = state.chatSessions;
          return { chatSessions: rest };
        });
      },

      streamBotResponse: async ({ chatId, userMessage, user, authorization }) => {
        const userApiKey = await getApi(user?.sub);

        const currentTime = new Date();
        const controller = new AbortController();

        const previousController = streamControllers.get(chatId);
        if (previousController) previousController.abort();
        streamControllers.set(chatId, controller);

        const botMessageId = `bot-${currentTime.getTime()}`;
        const apiKey = userApiKey?.payload?.api_key;
        const headers = {
          Authorization: `Bearer ${authorization}`,
          ...(apiKey ? { 'x-api-key': apiKey } : {}),
        };
        const url = `${process.env.REACT_APP_URL_CHAT_API}get?msg=${encodeURIComponent(userMessage)}`;

        let rawText = '';
        let flushTimeout: ReturnType<typeof setTimeout> | null = null;
        let didFinishSuccessfully = false;

        const flushMessage = () => {
          const { reasoning, reasoningSteps, answer } = parsePlanAndAnswer(rawText);
          set(state => {
            const session = state.chatSessions[chatId] ?? getDefaultSession();
            const updatedMessages = session.messages.map(msg =>
              msg.id === botMessageId ? { ...msg, message: answer, reasoning, reasoningSteps } : msg,
            );
            return {
              chatHistory: updatedMessages,
              chatSessions: {
                ...state.chatSessions,
                [chatId]: { ...session, messages: updatedMessages },
              },
            };
          });
        };

        const scheduleFlush = () => {
          if (flushTimeout) return;
          flushTimeout = setTimeout(() => {
            flushMessage();
            flushTimeout = null;
          }, STREAM_FLUSH_MS);
        };

        set(state => {
          const session = state.chatSessions[chatId] ?? getDefaultSession();
          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...session,
                messages: [
                  ...session.messages,
                  { id: botMessageId, sender: 'bot', message: '', timestamp: new Date() },
                ],
                isStreaming: true,
                botStatus: 'thinking',
                isChatActive: true,
              },
            },
          };
        });

        try {
          const response = await fetch(url, {
            method: 'GET',
            headers,
            credentials: 'include',
            signal: controller.signal,
            keepalive: true,
          });

          if (!response.ok) {
            throw new Error(
              response.status === 504
                ? 'The request is taking longer than expected. Please try a simpler question or try again later.'
                : 'Network response was not ok',
            );
          }

          if (!response.body) throw new Error('No response body received');

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let done = false;

          while (!done) {
            if (controller.signal.aborted) break;
            const { value, done: chunkDone } = await reader.read();
            done = chunkDone;
            if (value) {
              rawText += decoder.decode(value, { stream: true });
              scheduleFlush();
            }
          }

          if (flushTimeout) {
            clearTimeout(flushTimeout);
            flushTimeout = null;
          }
          flushMessage();

          didFinishSuccessfully = !controller.signal.aborted;
        } catch (error: unknown) {
          if (error instanceof Error && error.name === 'AbortError') {
            // Stopped by user — no-op
          } else {
            const errorMessage =
              error instanceof Error && error.message.includes('taking longer than expected')
                ? error.message
                : 'Sorry, there was an error processing your request. Please try again.';

            set(state => {
              const session = state.chatSessions[chatId] ?? getDefaultSession();
              const updatedMessages = [
                ...session.messages,
                {
                  id: `bot-error-${currentTime.getTime()}`,
                  sender: 'bot' as const,
                  message: errorMessage,
                  timestamp: new Date(),
                },
              ];
              return {
                chatHistory: updatedMessages,
                chatSessions: {
                  ...state.chatSessions,
                  [chatId]: { ...session, messages: updatedMessages },
                },
              };
            });
          }
        } finally {
          if (flushTimeout) clearTimeout(flushTimeout);
          if (streamControllers.get(chatId) === controller) streamControllers.delete(chatId);

          set(state => {
            const session = state.chatSessions[chatId] ?? getDefaultSession();
            return {
              chatSessions: {
                ...state.chatSessions,
                [chatId]: { ...session, isStreaming: false, botStatus: null },
              },
            };
          });

          if (didFinishSuccessfully && get().aidaNotificationsEnabled) {
            notifyAnswerReady(userMessage);
            if (!isInAidaView()) {
              emitInAppAnswerReadyAlert(userMessage, chatId);
            }
          }
        }
      },

      stopStreaming: (chatId, withInterruption) => {
        const controller = streamControllers.get(chatId);
        if (controller) {
          controller.abort();
          streamControllers.delete(chatId);
        }
        set(state => {
          const session = state.chatSessions[chatId];
          if (!session) return state;

          const updatedMessages = withInterruption
            ? session.messages.map((msg, i) => {
                const isLastBotMessage = i === session.messages.length - 1 && msg.sender === 'bot';
                if (isLastBotMessage) {
                  return {
                    ...msg,
                    interrupted: true,
                    message: msg.message.trim() || '_Request interrupted by user._',
                  };
                }
                return msg;
              })
            : session.messages;

          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...session,
                messages: updatedMessages,
                isStreaming: false,
                botStatus: null,
              },
            },
          };
        });
      },
    }),
    {
      name: 'chat-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: state => ({
        chatHistory: state.chatHistory,
        chatSessions: state.chatSessions,
        aidaNotificationsEnabled: state.aidaNotificationsEnabled,
      }),
    },
  ),
);
